/**
 * Kompozice aplikace: layout, plánovací pipeline, synchronizace
 * store ↔ UI ↔ mapa. Výpočty běží lokálně; reálné geometrie se
 * donačítají asynchronně a plán se zjemňuje.
 */
import { boundsAround, haversineKm } from '../core/geo';
import { assignBadges, sortItineraries, SORT_LABELS } from '../core/metrics';
import { createCustomMode, groupModes } from '../core/modes';
import { plan, type GeometryProvider } from '../core/planner';
import { formatDuration, formatHHMM, pragueMinutesOfDay, pragueParts } from '../core/time';
import { TIME_PRESETS, trafficFactorAt } from '../core/traffic';
import { buildDemoNetwork } from '../core/transit';
import { computeZoneGrid, ZONE_METRIC_HELP, ZONE_METRIC_LABELS } from '../core/zones';
import type {
  Itinerary,
  ModeProfile,
  PlanResult,
  PlanTuning,
  RoutingProfile,
  SortKey,
  TransitNetwork,
  ZoneGrid,
  ZoneMetric,
} from '../core/types';
import {
  cachedRoute,
  prefetchRoute,
  routingHealth,
  type ProviderPreference,
  type RoutingOptions,
} from '../data/routing';
import { envGolemioKey } from '../data/golemio';
import {
  activeParams,
  convertWindow,
  departMsOf,
  planWindowsOf,
  type Store,
  type TimeWindowInput,
} from '../state/store';
import { openOnboarding, openSettings } from './dialogs';
import { el, clear, debounce, fmtCzk, fmtCo2 } from './dom';
import { renderDetail } from './detail';
import { infoDot } from './help';
import { icon, iconOrEmoji } from './icons';
import { MapView } from './mapView';
import { openModeEditor } from './modeEditor';
import { createPointInputs } from './pointInputs';
import { openProfileManager, profileSwitcher } from './profilesUi';
import { openProgramManager } from './programsUi';
import { searchProfileBar } from './searchProfilesUi';
import { runProgram } from '../core/programRun';
import { renderResults } from './results';
import { openTransitEditor } from './transitEditor';

export function startApp(root: HTMLElement, store: Store): void {
  clear(root); // idempotentní start (HMR, opakovaná inicializace)
  /* ---------------- runtime stav mimo store ---------------- */
  let lastPlan: PlanResult | null = null;
  let sortedItineraries: Itinerary[] = [];
  let currentTransit: TransitNetwork = { stops: [], lines: [], maxAccessKm: 1.2, transferMin: 3, source: 'demo' };
  let zoneGrid: ZoneGrid | null = null;
  let planHash = '';
  let zoneHash = '';
  let planning = false;
  let stopPickCallback: ((lat: number, lng: number) => void) | null = null;

  /* ---------------- layout ---------------- */
  document.documentElement.setAttribute('data-theme', store.get().theme);

  const statusBox = el('div', { className: 'header__status', role: 'status' });

  const themeBtn = el(
    'button',
    {
      className: 'btn btn--icon',
      title: 'Přepnout tmavý/světlý režim (zkratka D)',
      'aria-label': 'Tmavý režim',
      'aria-pressed': String(store.get().theme === 'dark'),
      onclick: () => {
        const next = store.get().theme === 'dark' ? 'light' : 'dark';
        store.update({ theme: next });
        themeBtn.setAttribute('aria-pressed', String(next === 'dark'));
        document.documentElement.setAttribute('data-theme', next);
        mapView.setTheme(next);
      },
    },
    icon('theme', { size: 17 }),
  );

  const header = el(
    'header',
    { className: 'header' },
    el(
      'div',
      { className: 'header__logo' },
      el('span', { className: 'header__logo-badge', 'aria-hidden': 'true' }, icon('route', { size: 17 })),
      el('span', {}, 'OptiRoute'),
      el('span', { className: 'header__tagline' }, 'porovnávač tras A → B'),
    ),
    el('span', { className: 'header__spacer' }),
    statusBox,
    profileSwitcher(store, () => invalidatePlan()),
    el(
      'button',
      { className: 'btn btn--icon', title: 'Profily nastavení', 'aria-label': 'Spravovat profily', onclick: () => openProfileManager(store, () => invalidatePlan()) },
      icon('profile', { size: 17 }),
    ),
    el(
      'button',
      { className: 'btn btn--icon', title: 'Jízdní řády MHD (GTFS, ruční, demo)', 'aria-label': 'Jízdní řády MHD', onclick: () => openTransit() },
      icon('bus', { size: 17 }),
    ),
    el(
      'button',
      { className: 'btn btn--icon', title: 'Nastavení, sdílení, export', 'aria-label': 'Otevřít nastavení', onclick: () => openSettings(store, () => invalidatePlan()) },
      icon('settings', { size: 17 }),
    ),
    el(
      'button',
      { className: 'btn btn--icon', title: 'Nápověda', 'aria-label': 'Zobrazit nápovědu', onclick: () => openOnboarding(store) },
      icon('help', { size: 17 }),
    ),
    themeBtn,
  );

  const panel = el('aside', { className: 'panel', 'aria-label': 'Plánování trasy' });
  const mapWrap = el('div', { className: 'map-wrap' });
  const mapContainer = el('div', { id: 'map', role: 'application', 'aria-label': 'Mapa tras' });
  mapWrap.append(mapContainer);

  root.append(header, panel, mapWrap);

  /* ---------------- mapa ---------------- */
  const mapView = new MapView(mapContainer, mapWrap, store.get().theme, {
    onPick(target, p) {
      if (target === 'stop') {
        stopPickCallback?.(p.lat, p.lng);
        stopPickCallback = null;
        return;
      }
      store.update({
        [target]: { lat: p.lat, lng: p.lng },
        selectedItineraryId: null,
      } as never);
    },
    onMarkerDragged(which, p) {
      const cur = store.get()[which];
      store.update({
        [which]: { ...(cur ?? {}), lat: p.lat, lng: p.lng, address: undefined },
        selectedItineraryId: null,
      } as never);
    },
    onLayersChanged() {
      renderMap();
    },
  });

  /* ---------------- panel: body A/B ---------------- */
  const pointInputs = createPointInputs(store, {
    onPickOnMap(which) {
      mapView.setPickTarget(which, mapWrap);
    },
  });

  /* ---------------- panel: čas (okna od–do / v ±) ---------------- */
  /** Editor jednoho časového okna s přepínačem od–do ⇄ v ±. */
  const windowEditor = (
    label: string,
    get: () => TimeWindowInput,
    set: (w: TimeWindowInput) => void,
  ): { root: HTMLElement; refresh(): void } => {
    const body = el('div', { className: 'twin__body' });
    const modeBtns = el('div', { className: 'twin__switch', role: 'radiogroup', 'aria-label': `Způsob zadání: ${label}` });
    const root = el(
      'div',
      { className: 'twin' },
      el('div', { className: 'twin__head' }, el('span', { className: 'twin__label' }, label), modeBtns),
      body,
    );
    const dt = (value: string | null, aria: string, on: (v: string | null) => void): HTMLElement =>
      el('input', {
        className: 'input input--small twin__dt',
        type: 'datetime-local',
        value: value ?? '',
        'aria-label': aria,
        onchange: (e: Event) => on((e.target as HTMLInputElement).value || null),
      });
    const refresh = (): void => {
      const w = get();
      clear(modeBtns);
      for (const m of [{ id: 'range', t: 'od–do' }, { id: 'center', t: 'v ±' }] as const) {
        modeBtns.append(
          el(
            'button',
            {
              className: `twin__mode${w.mode === m.id ? ' twin__mode--on' : ''}`,
              type: 'button',
              role: 'radio',
              'aria-checked': String(w.mode === m.id),
              onclick: () => {
                set(convertWindow(get(), m.id));
                refresh();
              },
            },
            m.t,
          ),
        );
      }
      clear(body);
      if (w.mode === 'range') {
        body.append(
          el('span', { className: 'muted' }, 'od'),
          dt(w.from, `${label} od`, (v) => set({ ...get(), from: v })),
          el('span', { className: 'muted' }, 'do'),
          dt(w.to, `${label} do`, (v) => set({ ...get(), to: v })),
        );
      } else {
        body.append(
          el('span', { className: 'muted' }, 'v'),
          dt(w.center, `${label} v`, (v) => set({ ...get(), center: v })),
          el('span', { className: 'muted' }, '±'),
          el('input', {
            className: 'input input--small twin__tol',
            type: 'number',
            min: '0',
            max: '720',
            step: '5',
            value: String(w.tolMin),
            'aria-label': `${label}: tolerance ± minut`,
            onchange: (e: Event) => {
              const n = Number((e.target as HTMLInputElement).value);
              if (Number.isFinite(n)) set({ ...get(), tolMin: Math.max(0, Math.min(720, n)) });
            },
          }),
          el('span', { className: 'muted' }, 'min'),
        );
      }
    };
    refresh();
    return { root, refresh };
  };

  const depEditor = windowEditor('Odjezd', () => store.get().timeDep, (w) => {
    store.update({ timeDep: w });
    syncTimeUi();
  });
  const arrEditor = windowEditor('Příjezd', () => store.get().timeArr, (w) => {
    store.update({ timeArr: w });
    syncTimeUi();
  });

  /* ---------------- časový posuvník (simulace času) ---------------- */
  const timeLabel = el('span', { className: 'time-slider__label' });
  const timeSlider = el('input', {
    className: 'time-slider__range',
    type: 'range',
    min: '0',
    max: '1435',
    step: '5',
    'aria-label': 'Simulovaný čas odjezdu',
  }) as HTMLInputElement;

  /** Nastaví střed okna odjezdu na dnešní den (Europe/Prague) v daných minutách. */
  const setSimMinutes = (minutes: number): void => {
    const p = pragueParts(departMsOf(store.get()));
    const pad = (n: number): string => String(n).padStart(2, '0');
    const m = Math.max(0, Math.min(1435, Math.round(minutes / 5) * 5));
    store.update({
      timeDep: {
        mode: 'center',
        from: null,
        to: null,
        center: `${p.year}-${pad(p.month)}-${pad(p.day)}T${formatHHMM(m)}`,
        tolMin: store.get().timeDep.tolMin,
      },
    });
    syncTimeUi();
  };

  const depWindowEmpty = (): boolean => {
    const w = store.get().timeDep;
    return w.mode === 'center' ? !w.center : !w.from && !w.to;
  };

  const syncTimeUi = (): void => {
    const s = store.get();
    const minutes = Math.round(pragueMinutesOfDay(departMsOf(s)));
    timeSlider.value = String(Math.min(1435, Math.round(minutes / 5) * 5));
    timeLabel.textContent = formatHHMM(minutes);
    timeLabel.dataset.now = depWindowEmpty() ? '1' : '';
    depEditor.refresh();
    arrEditor.refresh();
    presetBox.querySelectorAll('button').forEach((b) => {
      const id = b.dataset.preset;
      const preset = TIME_PRESETS.find((x) => x.id === id);
      const on =
        preset &&
        (preset.minutes === null
          ? depWindowEmpty()
          : !depWindowEmpty() && Math.abs(minutes - preset.minutes) < 3);
      b.classList.toggle('time-preset--on', Boolean(on));
    });
  };

  // Plynulý posun: náhled hned, přepočet až po puštění/zastavení (debounce).
  const sliderCommit = debounce((v: number) => setSimMinutes(v), 180);
  timeSlider.addEventListener('input', () => {
    const v = Number(timeSlider.value);
    timeLabel.textContent = formatHHMM(v);
    sliderCommit(v);
  });

  const presetBox = el(
    'div',
    { className: 'time-presets', role: 'group', 'aria-label': 'Rychlé časy' },
    ...TIME_PRESETS.map((p) =>
      el(
        'button',
        {
          className: 'time-preset',
          type: 'button',
          'data-preset': p.id,
          onclick: () => {
            if (p.minutes === null) {
              // „Teď" = prázdné okno odjezdu → plánuje se od aktuální chvíle.
              store.update({ timeDep: { ...store.get().timeDep, mode: 'center', from: null, to: null, center: null } });
              syncTimeUi();
            } else {
              setSimMinutes(p.minutes);
            }
          },
        },
        p.label,
      ),
    ),
  );

  const timeCard = el(
    'section',
    { className: 'card', 'aria-label': 'Čas cesty' },
    el(
      'h2',
      { className: 'card__title' },
      'Kdy',
      infoDot(
        store,
        'Odjezd i příjezd se vždy chápou jako okno od–do; přepínačem volíte zadání rozsahem, nebo „v čas ± tolerance". Nevyplněná mez = bez omezení; vše prázdné = odjezd od teď. Posuvník nastavuje střed okna odjezdu. Když okna nesplní žádná trasa, ukážeme nejbližší možné.',
      ),
    ),
    el('div', { className: 'time-slider' }, timeSlider, timeLabel),
    presetBox,
    depEditor.root,
    arrEditor.root,
    el('div', { className: 'hint' }, 'Časy se počítají v pásmu Europe/Prague.'),
  );
  syncTimeUi();

  /* ---------------- panel: režimy ---------------- */
  const modeGrid = el('div', { className: 'mode-grid', role: 'group', 'aria-label': 'Porovnávané dopravní režimy' });
  let modesSignature = '';

  const renderModes = (): void => {
    modesSignature = store.get().modes.map((m) => `${m.id}:${m.enabled}:${m.name}:${m.color}`).join('|');
    clear(modeGrid);
    for (const { group, modes: groupModesList } of groupModes(store.get().modes)) {
      const allOn = groupModesList.every((m) => m.enabled);
      modeGrid.append(
        el(
          'div',
          { className: 'mode-group__head' },
          el('span', { className: 'mode-group__name' }, group.name),
          el(
            'button',
            {
              className: 'mode-group__toggle',
              type: 'button',
              'aria-label': `${allOn ? 'Vypnout' : 'Zapnout'} oblast ${group.name}`,
              onclick: () => {
                for (const m of groupModesList) store.updateMode(m.id, { enabled: !allOn });
                invalidatePlan();
              },
            },
            allOn ? 'vypnout vše' : 'zapnout vše',
          ),
        ),
      );
      for (const m of groupModesList) renderModeChip(m);
    }
  };

  const renderModeChip = (m: ModeProfile): void => {
    {
      modeGrid.append(
        el(
          'div',
          {
            className: 'mode-chip',
            style: `--chip-color:${m.color}`,
            role: 'button',
            tabindex: '0',
            'aria-pressed': String(m.enabled),
            'aria-label': `${m.name}${m.enabled ? ' — zapnuto' : ' — vypnuto'}`,
            onclick: () => {
              store.updateMode(m.id, { enabled: !m.enabled });
              invalidatePlan();
            },
            onkeydown: ((e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                store.updateMode(m.id, { enabled: !m.enabled });
                invalidatePlan();
              }
            }) as EventListener,
          },
          el('span', { className: 'mode-chip__dot', 'aria-hidden': 'true' }),
          el('span', { className: 'mode-chip__icon', 'aria-hidden': 'true' }, iconOrEmoji(m.icon, { size: 18 })),
          el(
            'span',
            { className: 'mode-chip__name' },
            m.name,
            el('div', { className: 'mode-chip__speed' }, modeSubtitle(m.kind, m.speedKmh)),
          ),
          el(
            'button',
            {
              className: 'mode-chip__gear',
              title: `Parametry: ${m.name}`,
              'aria-label': `Upravit parametry režimu ${m.name}`,
              onclick: ((e: MouseEvent) => {
                e.stopPropagation();
                openModeEditor(store, m.id, () => {
                  renderModes();
                  invalidatePlan();
                });
              }) as EventListener,
            },
            icon('settings', { size: 13 }),
          ),
        ),
      );
    }
  };
  renderModes();

  const modesCard = el(
    'section',
    { className: 'card', 'aria-label': 'Dopravní režimy' },
    el(
      'h2',
      { className: 'card__title' },
      'Co porovnat',
      el('span', { className: 'spacer' }),
      el(
        'button',
        {
          className: 'btn btn--small',
          onclick: () => {
            const mode = createCustomMode();
            store.update({ modes: [...store.get().modes, mode] });
            renderModes();
            openModeEditor(store, mode.id, () => {
              renderModes();
              invalidatePlan();
            });
          },
        },
        icon('plus', { size: 13 }),
        ' Vlastní režim',
      ),
    ),
    modeGrid,
    el('div', { className: 'hint' }, 'Klik zapne/vypne režim, ozubené kolečko otevře parametry (rychlost, cena, emise…).'),
  );

  /* ---------------- panel: řazení a filtry ---------------- */
  const sortSelect = el(
    'select',
    {
      className: 'select',
      'aria-label': 'Řadit trasy podle',
      onchange: (e: Event) => {
        store.update({ sort: { ...store.get().sort, key: (e.target as HTMLSelectElement).value as SortKey } });
      },
    },
    ...Object.entries(SORT_LABELS).map(([key, label]) =>
      el('option', { value: key, selected: key === store.get().sort.key }, label),
    ),
  ) as HTMLSelectElement;

  const carFreeCheck = el('input', {
    type: 'checkbox',
    checked: store.get().sort.carFreeOnly,
    onchange: (e: Event) => store.update({ sort: { ...store.get().sort, carFreeOnly: (e.target as HTMLInputElement).checked } }),
  }) as HTMLInputElement;

  const accessibleCheck = el('input', {
    type: 'checkbox',
    checked: store.get().sort.accessibleOnly,
    onchange: (e: Event) => store.update({ sort: { ...store.get().sort, accessibleOnly: (e.target as HTMLInputElement).checked } }),
  }) as HTMLInputElement;

  const weightSliders = (['time', 'cost', 'comfort', 'emissions'] as const).map((k) => {
    const labels: Record<string, string> = { time: 'Čas', cost: 'Cena', comfort: 'Komfort', emissions: 'Emise' };
    const input = el('input', {
      type: 'range',
      min: '0',
      max: '100',
      value: String(Math.round(store.get().sort.weights[k] * 100)),
      'aria-label': `Váha: ${labels[k]}`,
      onchange: (e: Event) => {
        const v = Number((e.target as HTMLInputElement).value) / 100;
        store.update({ sort: { ...store.get().sort, weights: { ...store.get().sort.weights, [k]: v } } });
      },
    });
    return el('label', { className: 'row', style: 'font-size:12px' }, el('span', { style: 'width:62px' }, labels[k]), input);
  });

  /* ---------------- vlastní programy: ovládání ---------------- */
  const progControls = el('div', { style: 'margin-top:8px' });
  const renderProgControls = (): void => {
    clear(progControls);
    const s = store.get();
    const progs = s.customPrograms;
    const select = (label: string, kind: 'sort' | 'search', value: string | null, on: (id: string | null) => void): HTMLElement | null => {
      const opts = progs.filter((x) => x.kind === kind);
      if (opts.length === 0) return null;
      return el('div', { className: 'row', style: 'margin-top:4px' },
        el('span', { className: 'muted', style: 'width:96px' }, label),
        el('select', { className: 'select select--small grow', 'aria-label': label, onchange: (e: Event) => on((e.target as HTMLSelectElement).value || null) },
          el('option', { value: '', selected: !value }, '— žádné —'),
          ...opts.map((x) => el('option', { value: x.id, selected: x.id === value }, x.name)),
        ),
      );
    };
    const sortSel = select('Vlastní řazení', 'sort', s.customSortId, (id) => { store.update({ customSortId: id }); invalidatePrograms(); });
    const searchSel = select('Vlastní hledání', 'search', s.customSearchId, (id) => { store.update({ customSearchId: id }); invalidatePrograms(); });
    if (sortSel) progControls.append(sortSel);
    if (searchSel) progControls.append(searchSel);
    const filters = progs.filter((x) => x.kind === 'filter');
    if (filters.length > 0) {
      progControls.append(el('div', { className: 'row row--wrap', style: 'margin-top:4px' },
        ...filters.map((f) => el('label', { className: 'switch' },
          el('input', { type: 'checkbox', checked: s.customFilterIds.includes(f.id), onchange: (e: Event) => {
            const on = (e.target as HTMLInputElement).checked;
            const cur = store.get().customFilterIds.filter((x) => x !== f.id);
            store.update({ customFilterIds: on ? [...cur, f.id] : cur });
            invalidatePrograms();
          } }),
          el('span', {}, f.name),
        )),
      ));
    }
  };

  const sortCard = el(
    'section',
    { className: 'card', 'aria-label': 'Řazení a filtry' },
    el(
      'h2',
      { className: 'card__title' },
      'Řazení a filtry',
      el('span', { className: 'spacer' }),
      el('button', { className: 'btn btn--small', onclick: () => openProgramManager(store, () => sortedItineraries, () => { renderProgControls(); invalidatePrograms(); }) }, icon('sliders', { size: 12 }), ' Programy'),
      infoDot(store, 'Vlastní programy: naprogramujte si řazení, filtr i celé hledání — vizuálně, výrazy, nebo v JavaScriptu. Běží v sandboxu s timeoutem.'),
    ),
    sortSelect,
    (renderProgControls(), progControls),
    el(
      'div',
      { className: 'row row--wrap', style: 'margin-top:8px' },
      el('label', { className: 'switch' }, carFreeCheck, el('span', {}, 'Bez auta')),
      el('label', { className: 'switch' }, accessibleCheck, el('span', {}, 'Bezbariérově')),
    ),
    el(
      'details',
      { style: 'margin-top:8px' },
      el('summary', { className: 'muted', style: 'cursor:pointer' }, 'Váhy doporučení'),
      el('div', { style: 'display:flex;flex-direction:column;gap:4px;margin-top:6px' }, ...weightSliders),
    ),
  );

  /* ---------------- panel: zóny ---------------- */
  const zoneMetricSelect = el(
    'select',
    {
      className: 'select',
      'aria-label': 'Metrika zón',
      onchange: (e: Event) => {
        store.update({ zoneMetric: (e.target as HTMLSelectElement).value as ZoneMetric });
        updateZoneHint();
      },
    },
    ...Object.entries(ZONE_METRIC_LABELS).map(([key, label]) =>
      el('option', { value: key, selected: key === store.get().zoneMetric }, label),
    ),
  ) as HTMLSelectElement;

  const zonesCheck = el('input', {
    type: 'checkbox',
    checked: store.get().zonesEnabled,
    onchange: (e: Event) => store.update({ zonesEnabled: (e.target as HTMLInputElement).checked }),
  }) as HTMLInputElement;

  // Výběr režimů, které vstupují do vybarvení mapy.
  const zoneModesBox = el('div', { className: 'row row--wrap', role: 'group', 'aria-label': 'Režimy pro zóny', style: 'gap:4px;margin-top:8px' });
  const renderZoneModes = (): void => {
    clear(zoneModesBox);
    const s = store.get();
    const enabled = s.modes.filter((m) => m.enabled);
    for (const { group, modes: gm } of groupModes(enabled)) {
      zoneModesBox.append(el('span', { className: 'zone-group__name' }, group.name));
      for (const m of gm) renderZoneChip(m);
    }
    if (enabled.length === 0) {
      zoneModesBox.append(el('span', { className: 'muted' }, 'Zapněte alespoň jeden režim výše.'));
    }
  };

  const renderZoneChip = (m: ModeProfile): void => {
    const s = store.get();
    {
      const included = !s.zoneModeIds || s.zoneModeIds.includes(m.id);
      zoneModesBox.append(
        el(
          'button',
          {
            className: `zone-mode-chip${included ? ' zone-mode-chip--on' : ''}`,
            type: 'button',
            'aria-pressed': String(included),
            style: `--chip-color:${m.color}`,
            onclick: () => {
              const cur = store.get();
              const all = cur.modes.filter((x) => x.enabled).map((x) => x.id);
              let next = cur.zoneModeIds ? [...cur.zoneModeIds] : all;
              next = next.includes(m.id) ? next.filter((x) => x !== m.id) : [...next, m.id];
              // Všechny vybrané = null (sleduje budoucí zapnuté režimy automaticky).
              store.update({ zoneModeIds: all.every((id) => next.includes(id)) ? null : next });
              renderZoneModes();
            },
          },
          iconOrEmoji(m.icon, { size: 13 }),
          ` ${m.name}`,
        ),
      );
    }
  };
  renderZoneModes();

  const zoneHint = el('div', { className: 'hint' });
  const updateZoneHint = (): void => {
    zoneHint.textContent =
      ZONE_METRIC_HELP[store.get().zoneMetric] +
      ' Vše je cesta z daného místa DO CÍLE B, odhadovým modelem pro simulovaný čas. Dosah barev omezují limity vzdálenosti režimů (pěšky 12 km…) — upravíte je ozubeným kolečkem u režimu, 0 = bez limitu; u hranice limitu barva plynule bledne.';
  };
  updateZoneHint();

  const zonesCard = el(
    'section',
    { className: 'card', 'aria-label': 'Zóny výhodnosti' },
    el(
      'h2',
      { className: 'card__title' },
      'Zóny výhodnosti',
      infoDot(
        store,
        'Vybarví mapu podle toho, ODKUD se do cíle B vyplatí který způsob dopravy (bod A do výpočtu nevstupuje). Vyberte metriku a režimy; barvy vysvětluje legenda. Pokud chcete větší dosah, zvyšte limit vzdálenosti režimu (0 = bez limitu).',
      ),
    ),
    el('label', { className: 'switch' }, zonesCheck, el('span', {}, 'Vybarvit mapu podle metriky')),
    el('div', { style: 'margin-top:8px' }, zoneMetricSelect),
    zoneModesBox,
    zoneHint,
  );

  /* ---------------- panel: výsledky a detail ---------------- */
  const warningsBox = el('div', { style: 'display:flex;flex-direction:column;gap:6px' });
  const summaryBox = el('div', { className: 'muted', style: 'margin-bottom:4px' });
  const resultsBox = el('div', { className: 'results' });
  const resultsCard = el(
    'section',
    { className: 'card', 'aria-label': 'Výsledky porovnání' },
    el('h2', { className: 'card__title' }, 'Trasy k porovnání'),
    warningsBox,
    summaryBox,
    resultsBox,
  );

  const detailCard = el('section', { className: 'card detail', 'aria-label': 'Detail trasy', style: 'display:none' });

  const spBar = searchProfileBar(store, () => {
    renderProgControls();
    invalidatePrograms();
    invalidatePlan();
  });
  panel.append(pointInputs.root, spBar, timeCard, modesCard, sortCard, zonesCard, resultsCard, detailCard);

  /* ---------------- konfigurace routování z parametrů ---------------- */
  const routingOpts = (): RoutingOptions => {
    const p = activeParams(store.get());
    return {
      online: p.get('routing.online') && navigator.onLine !== false,
      provider: p.get('routing.provider') as ProviderPreference,
      osrmEndpoint: p.get('routing.osrmEndpoint') || undefined,
      orsApiKey: p.get('routing.orsApiKey') || undefined,
      timeoutMs: p.get('routing.timeoutMs'),
    };
  };

  /** Síť MHD podle zvoleného zdroje. 'none' = prázdná síť (PT se neplánuje). */
  const resolveTransit = (): TransitNetwork => {
    const s = store.get();
    const p = activeParams(s);
    const base: TransitNetwork | null =
      s.transitSource === 'manual'
        ? s.manualTransit
        : s.transitSource === 'gtfs'
          ? s.gtfsTransit
          : s.transitSource === 'demo' && s.origin && s.destination
            ? buildDemoNetwork(s.origin, s.destination)
            : null;
    if (!base) return { stops: [], lines: [], maxAccessKm: 1.2, transferMin: 3, source: 'demo' };
    // Docházku a přestupní rezervu řídí parametry (profil je může přepsat).
    return {
      ...base,
      maxAccessKm: p.get('transit.maxAccessKm'),
      transferMin: p.get('transit.transferMin'),
    };
  };

  /* ---------------- stavové čipy ---------------- */
  const renderStatus = (): void => {
    clear(statusBox);
    const s = store.get();
    const offline = navigator.onLine === false;
    const health = routingHealth(routingOpts());
    statusBox.append(
      el(
        'span',
        {
          className: 'status-chip',
          'data-tone': health === 'ok' ? 'good' : 'warn',
          title:
            health === 'ok'
              ? 'Geometrie a délky tras přicházejí z routovací služby (OpenStreetMap data).'
              : 'Routovací služba není dostupná — trasy jsou odhad (vzdušná čára × nepřímost).',
        },
        health === 'ok' ? 'Reálné trasy' : 'Odhadový model',
      ),
      el(
        'span',
        {
          className: 'status-chip',
          'data-tone': s.transitSource === 'gtfs' || s.transitSource === 'manual' ? 'good' : s.transitSource === 'demo' ? 'warn' : undefined,
          title: 'Zdroj jízdních řádů MHD. Změníte ho ikonou autobusu vpravo nahoře.',
        },
        s.transitSource === 'gtfs'
          ? `MHD: GTFS${s.gtfsFeedName ? ` (${s.gtfsFeedName})` : ''}`
          : s.transitSource === 'manual'
            ? 'MHD: vlastní jízdní řád'
            : s.transitSource === 'demo'
              ? 'MHD: demo (odhad)'
              : 'MHD: nenastaveno',
      ),
    );
    if (offline) statusBox.append(el('span', { className: 'status-chip', 'data-tone': 'warn' }, 'Offline'));
  };

  /* ---------------- plánovací pipeline ---------------- */

  const geometryProvider: GeometryProvider = (profile, from, to) => {
    if (!routingOpts().online) return undefined;
    return cachedRoute(profile, from, to);
  };

  /** Doladění výpočtu z parametrů a simulovaného času. */
  const planTuning = (departMs: number): PlanTuning => {
    const p = activeParams(store.get());
    return {
      carTimeFactor: trafficFactorAt(departMs, {
        enabled: p.get('car.peakEnabled'),
        morningFromH: p.get('car.peakMorningFrom'),
        morningToH: p.get('car.peakMorningTo'),
        morningFactor: p.get('car.peakMorningFactor'),
        afternoonFromH: p.get('car.peakAfternoonFrom'),
        afternoonToH: p.get('car.peakAfternoonTo'),
        afternoonFactor: p.get('car.peakAfternoonFactor'),
      }),
      useProviderDuration: p.get('routing.useProviderDuration'),
      defaultFareCzk: p.get('transit.fareCzk'),
      maxTransfers: p.get('transit.maxTransfers'),
    };
  };

  function computePlanHash(): string {
    const s = store.get();
    return JSON.stringify([
      s.origin,
      s.destination,
      s.timeDep,
      s.timeArr,
      s.strategy,
      s.modes,
      s.transitSource,
      s.manualTransit,
      s.gtfsTransit ? s.gtfsTransit.lines.length + ':' + s.gtfsTransit.stops.length : null,
      s.paramValues,
      s.activeProfileId,
      s.profiles.find((x) => x.id === s.activeProfileId)?.overrides ?? null,
    ]);
  }

  function invalidatePlan(): void {
    planHash = '';
    zoneHash = '';
    scheduleReplan();
  }

  /* ---------------- vlastní programy: běh a stav ---------------- */
  let progState: { key: string; ids: string[] | null; error: string | null; builtinOrder: boolean } = {
    key: '',
    ids: null,
    error: null,
    builtinOrder: true,
  };

  function invalidatePrograms(): void {
    progState = { key: '', ids: null, error: null, builtinOrder: true };
    renderProgControls();
    renderResultsPanel();
  }

  async function applyPrograms(base: Itinerary[], key: string): Promise<void> {
    const s = store.get();
    const dep = departMsOf(s);
    let err: string | null = null;
    let ids: string[] | null = null;
    let builtinOrder = true;
    const search = s.customPrograms.find((p) => p.id === s.customSearchId && p.kind === 'search');
    if (search) {
      const r = await runProgram(search, base, dep);
      if (r.error) err = `${search.name}: ${r.error}`;
      else {
        ids = r.ids;
        builtinOrder = false;
      }
    } else {
      let list = base;
      for (const fid of s.customFilterIds) {
        const f = s.customPrograms.find((p) => p.id === fid && p.kind === 'filter');
        if (!f) continue;
        const r = await runProgram(f, list, dep);
        if (r.error) {
          err = `${f.name}: ${r.error}`;
          continue;
        }
        const keep = new Set(r.ids);
        list = list.filter((t) => keep.has(t.id));
      }
      const sortP = s.customPrograms.find((p) => p.id === s.customSortId && p.kind === 'sort');
      if (sortP) {
        const r = await runProgram(sortP, list, dep);
        if (r.error) err = `${sortP.name}: ${r.error}`;
        else {
          ids = r.ids;
          builtinOrder = false;
        }
      }
      if (!ids) ids = list.map((t) => t.id);
    }
    if (progState.key !== key) return; // mezitím se změnilo zadání
    progState = { key, ids, error: err, builtinOrder };
    renderResultsPanel();
  }

  const scheduleReplan = debounce(() => replan(), 120);

  function replan(): void {
    const s = store.get();
    const hash = computePlanHash();
    if (hash === planHash) {
      renderOutputs();
      return;
    }
    planHash = hash;

    if (!s.origin || !s.destination) {
      lastPlan = null;
      sortedItineraries = [];
      zoneGrid = null;
      renderOutputs();
      return;
    }

    planning = true;
    renderResultsPanel(); // skeleton

    currentTransit = resolveTransit();

    const departMs = departMsOf(s);
    lastPlan = plan(
      {
        origin: s.origin,
        destination: s.destination,
        departMs,
        windows: planWindowsOf(s),
        strategy: s.strategy,
        modes: s.modes,
        transit: currentTransit,
        tuning: planTuning(departMs),
      },
      geometryProvider,
    );
    planning = false;

    // Vybraná trasa, která už neexistuje → zrušit výběr.
    if (s.selectedItineraryId && !lastPlan.itineraries.some((i) => i.id === s.selectedItineraryId)) {
      store.update({ selectedItineraryId: null, soloRoute: false });
      return; // subscribe vyvolá nový render
    }

    renderOutputs();
    prefetchGeometries();
    scheduleZones();
  }

  /** Donačtení reálných geometrií pro segmenty, které zatím mají odhad. */
  const prefetchGeometries = debounce(async () => {
    const s = store.get();
    const opts = routingOpts();
    if (!opts.online || !lastPlan) return;
    const pairs: Array<{ profile: RoutingProfile; from: { lat: number; lng: number }; to: { lat: number; lng: number } }> = [];
    for (const it of lastPlan.itineraries) {
      for (const seg of it.segments) {
        if (seg.realGeometry || seg.geometry.length < 2) continue;
        if (seg.kind === 'walk') pairs.push({ profile: 'foot', from: seg.from, to: seg.to });
        else if (seg.kind === 'ride') {
          const mode = s.modes.find((m) => m.id === seg.refId);
          if (mode?.routingProfile) pairs.push({ profile: mode.routingProfile, from: seg.from, to: seg.to });
        }
      }
    }
    const unique = pairs.slice(0, 14);
    let improved = false;
    await Promise.all(
      unique.map(async (p) => {
        // přeskoč miniaturní úseky — odhad stačí a šetříme službu
        if (haversineKm(p.from, p.to) < 0.08) return;
        if (await prefetchRoute(p.profile, p.from, p.to, opts)) improved = true;
      }),
    );
    renderStatus();
    if (improved) {
      planHash = ''; // vynutit přepočet s novými geometriemi
      replan();
    }
  }, 250);

  /* ---------------- zóny ---------------- */
  /**
   * Kalibrace nepřímosti z reálných tras: pro režimy s reálnou geometrií
   * známe skutečnou délku A→B — poměr ke „vzdušná × nepřímost" přenáší
   * místní terén do zónového odhadu (zóny pak souhlasí s výsledky).
   */
  const zoneCalibration = (): Record<string, number> => {
    const out: Record<string, number> = {};
    const s = store.get();
    if (!lastPlan || !s.origin || !s.destination || lastPlan.crowKm < 0.2) return out;
    const all = [...lastPlan.itineraries, ...lastPlan.nearest];
    for (const it of all) {
      const mode = s.modes.find((m) => m.id === it.modeId);
      if (!mode || mode.kind === 'pt' || mode.kind === 'combined') continue;
      const ride = it.segments.find((seg) => seg.kind === 'ride' && seg.realGeometry);
      if (!ride) continue;
      const estimatedKm = lastPlan.crowKm * mode.circuity;
      if (estimatedKm <= 0) continue;
      // Rozumné meze: extrémní poměry (objížďka přes řeku…) nepřenášíme naplno.
      out[mode.id] = Math.round(Math.min(3, Math.max(0.7, it.distanceKm / estimatedKm)) * 100) / 100;
    }
    return out;
  };

  const scheduleZones = debounce(() => {
    const s = store.get();
    if (!s.zonesEnabled || !s.destination) {
      zoneGrid = null;
      zoneHash = '';
      renderMap();
      return;
    }
    const p = activeParams(s);
    const departMs = departMsOf(s);
    const calib = zoneCalibration();
    // Čas kvantujeme po 5 minutách — posuvník tak přepočítává plynule, ale ne zbytečně.
    const timeKey = p.get('zones.timeAware') ? Math.round(departMs / 300_000) : 'static';
    const hash = JSON.stringify([
      s.origin, s.destination, s.modes, s.transitSource, s.manualTransit,
      s.gtfsTransit ? s.gtfsTransit.lines.length : 0,
      s.zoneMetric, s.zoneModeIds, s.sort.weights, timeKey, calib,
      p.get('zones.resolution'), s.activeProfileId,
    ]);
    if (hash === zoneHash && zoneGrid) {
      renderMap();
      return;
    }
    zoneHash = hash;
    const st = store.get();
    const pp = activeParams(st);
    const pts = [st.destination!, ...(st.origin ? [st.origin] : [])];
    const crow = st.origin ? haversineKm(st.origin, st.destination!) : 3;
    const pad = Math.max(1.5, crow * 0.45);
    const params = {
      bounds: boundsAround(pts, pad),
      destination: st.destination!,
      modes: st.modes,
      transit: currentTransit,
      metric: st.zoneMetric,
      cols: pp.get('zones.resolution'),
      modeIds: st.zoneModeIds,
      departMs: pp.get('zones.timeAware') ? departMsOf(st) : undefined,
      weights: st.sort.weights,
      circuityCalibration: calib,
    };
    // Výpočet běží ve web workeru — i nad reálnou sítí (tisíce zastávek)
    // se UI neseká. Zastaralé výsledky zahazujeme podle pořadového čísla.
    const seq = ++zoneSeq;
    computeZonesAsync(params).then((grid) => {
      if (seq !== zoneSeq) return; // mezitím přišel novější požadavek
      zoneGrid = grid;
      renderMap();
    });
  }, 200);

  let zoneSeq = 0;
  let zonesWorker: Worker | null = null;
  let zonesWorkerBroken = false;

  function computeZonesAsync(params: Parameters<typeof computeZoneGrid>[0]): Promise<ZoneGrid | null> {
    if (typeof Worker === 'undefined' || zonesWorkerBroken) {
      return new Promise((resolve) =>
        window.setTimeout(() => resolve(safeComputeZones(params)), 15),
      );
    }
    return new Promise((resolve) => {
      try {
        if (!zonesWorker) {
          zonesWorker = new Worker(new URL('../workers/zonesWorker.ts', import.meta.url), { type: 'module' });
        }
        const worker = zonesWorker;
        const seq = zoneSeq;
        const onMsg = (e: MessageEvent<{ seq: number; ok: boolean; grid?: ZoneGrid }>): void => {
          if (e.data.seq !== seq) return; // odpověď na starší požadavek
          worker.removeEventListener('message', onMsg);
          resolve(e.data.ok ? (e.data.grid ?? null) : safeComputeZones(params));
        };
        worker.addEventListener('message', onMsg);
        worker.onerror = () => {
          // Worker nejde (CSP, file://…) → synchronní fallback, ale jen jednou.
          zonesWorkerBroken = true;
          worker.terminate();
          zonesWorker = null;
          resolve(safeComputeZones(params));
        };
        worker.postMessage({ seq, params });
      } catch {
        zonesWorkerBroken = true;
        resolve(safeComputeZones(params));
      }
    });
  }

  function safeComputeZones(params: Parameters<typeof computeZoneGrid>[0]): ZoneGrid | null {
    try {
      return computeZoneGrid(params);
    } catch {
      return null;
    }
  }

  /* ---------------- rendering ---------------- */

  function renderOutputs(): void {
    pointInputs.refresh();
    renderStatus();
    // Čipy režimů překreslíme, jen když se režimy opravdu změnily (kvůli fokusu).
    const s = store.get();
    const sig = s.modes.map((m) => `${m.id}:${m.enabled}:${m.name}:${m.color}:${m.icon}`).join('|');
    if (sig !== modesSignature) {
      renderModes();
      renderZoneModes();
    }
    // Synchronizace ovládacích prvků se stavem (profil je mohl přepnout).
    if (sortSelect.value !== s.sort.key) sortSelect.value = s.sort.key;
    if (zoneMetricSelect.value !== s.zoneMetric) {
      zoneMetricSelect.value = s.zoneMetric;
      updateZoneHint();
    }
    carFreeCheck.checked = s.sort.carFreeOnly;
    accessibleCheck.checked = s.sort.accessibleOnly;
    syncTimeUi();
    renderResultsPanel();
    renderMap();
  }

  function renderResultsPanel(): void {
    const s = store.get();
    clear(warningsBox);
    clear(summaryBox);

    if (planning) {
      clear(resultsBox);
      resultsBox.append(el('div', { className: 'skeleton' }), el('div', { className: 'skeleton' }));
      return;
    }

    if (!s.origin || !s.destination) {
      clear(resultsBox);
      resultsBox.append(
        el(
          'div',
          { className: 'empty-state' },
          el('div', { className: 'empty-state__icon', 'aria-hidden': 'true' }, icon('map', { size: 30 })),
          el('strong', {}, 'Zadejte start a cíl'),
          el('p', {}, 'Vyhledejte adresu, klikněte do mapy, nebo použijte aktuální polohu. Pak porovnáme všechny vybrané způsoby dopravy.'),
        ),
      );
      renderDetail(detailCard, null, false, detailCallbacks);
      return;
    }

    if (!lastPlan) return;

    for (const w of lastPlan.warnings) {
      warningsBox.append(el('div', { className: 'alert alert--warn', role: 'alert' }, w));
    }

    // MHD bez zdroje dat: srozumitelný stav s akcí, ne tiché demo.
    const ptWanted = s.modes.some((m) => m.enabled && (m.kind === 'pt' || m.kind === 'combined'));
    if (ptWanted && s.transitSource === 'none') {
      warningsBox.append(
        el(
          'div',
          { className: 'alert alert--info' },
          'Chybí zdroj jízdních řádů MHD — veřejná doprava se zatím neporovnává. ',
          el(
            'button',
            { className: 'btn btn--small', style: 'margin-left:6px', onclick: () => openTransit() },
            'Vybrat zdroj',
          ),
        ),
      );
    }

    // Nic nesplňuje okna → ukážeme nejbližší možné trasy (s vysvětlením u řádků).
    const baseList = lastPlan.itineraries.length > 0 ? lastPlan.itineraries : lastPlan.nearest;

    // Vlastní programy (hledání > filtry+řazení) — sandbox, async, cache dle klíče.
    const anyProg = s.customSearchId || s.customSortId || s.customFilterIds.length > 0;
    let workList = baseList;
    let programOrder = false;
    if (anyProg) {
      const key = JSON.stringify([s.customSearchId, s.customSortId, s.customFilterIds, baseList.map((i) => i.id), s.customPrograms]);
      if (progState.key !== key) {
        progState = { key, ids: null, error: null, builtinOrder: true };
        void applyPrograms(baseList, key);
      }
      if (progState.error) {
        warningsBox.append(el('div', { className: 'alert alert--warn', role: 'alert' }, `Vlastní program: ${progState.error}`));
      }
      if (progState.ids) {
        if (progState.builtinOrder) {
          const keep = new Set(progState.ids);
          workList = baseList.filter((t) => keep.has(t.id));
        } else {
          workList = progState.ids
            .map((pid) => baseList.find((t) => t.id === pid))
            .filter((t): t is Itinerary => Boolean(t));
          programOrder = true;
        }
      }
    }
    sortedItineraries = programOrder ? workList : sortItineraries(workList, s.sort);
    assignBadges(sortedItineraries, s.sort.weights);

    if (sortedItineraries.length > 0) {
      const fastest = Math.min(...sortedItineraries.map((i) => i.totalMin));
      const cheapest = Math.min(...sortedItineraries.map((i) => i.cost));
      const greenest = Math.min(...sortedItineraries.map((i) => i.emissions));
      summaryBox.textContent = `${sortedItineraries.length} variant · nejrychleji ${formatDuration(fastest)} · od ${fmtCzk(cheapest)} · emise od ${fmtCo2(greenest)} · vzdušně ${lastPlan.crowKm.toFixed(1)} km`;
    } else if (lastPlan.itineraries.length > 0) {
      warningsBox.append(
        el('div', { className: 'alert alert--info' }, 'Aktivní filtry skryly všechny trasy. Zkuste vypnout „Bez auta“ nebo „Bezbariérově“.'),
      );
    }

    renderResults(resultsBox, sortedItineraries, lastPlan.infeasible, s.selectedItineraryId, (id) => {
      const cur = store.get();
      store.update({ selectedItineraryId: cur.selectedItineraryId === id ? null : id, soloRoute: false });
    });

    const selected = sortedItineraries.find((i) => i.id === s.selectedItineraryId) ?? null;
    renderDetail(detailCard, selected, s.soloRoute, detailCallbacks, liveConfigFor(selected));
  }

  /** Živé odjezdy (Golemio) pro vybranou trasu — jen PID GTFS + zapnuto + klíč. */
  function liveConfigFor(selected: Itinerary | null) {
    if (!selected) return null;
    const s = store.get();
    const p = activeParams(s);
    if (!p.get('transit.liveEnabled') || s.transitSource !== 'gtfs') return null;
    const key = p.get('transit.golemioKey') || envGolemioKey();
    if (!key) return null;
    const firstRide = selected.segments.find((seg) => seg.kind === 'transit');
    if (!firstRide) return null;
    // GTFS id nástupní zastávky dohledáme podle souřadnic segmentu.
    const stop = currentTransit.stops.find(
      (st) => Math.abs(st.lat - firstRide.from.lat) < 1e-6 && Math.abs(st.lng - firstRide.from.lng) < 1e-6,
    );
    if (!stop) return null;
    return { stopIds: [stop.id], apiKey: key, stopName: stop.name };
  }

  const detailCallbacks = {
    onClose: () => store.update({ selectedItineraryId: null, soloRoute: false }),
    onSoloToggle: (solo: boolean) => store.update({ soloRoute: solo }),
    onFocusMap: () => {
      const s = store.get();
      mapView.fitToPoints(s.origin, s.destination);
      mapWrap.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    },
  };

  function renderMap(): void {
    const s = store.get();
    mapView.setPoints(s.origin, s.destination);
    mapView.renderRoutes(sortedItineraries, s.selectedItineraryId, s.soloRoute);
    const ptOn = s.modes.some((m) => (m.kind === 'pt' || m.kind === 'combined') && m.enabled);
    mapView.renderStops(currentTransit, ptOn);
    mapView.renderZones(s.zonesEnabled ? zoneGrid : null, s.modes, activeParams(s).get('zones.opacity'));

    const segLegend = sortedItineraries.flatMap((it) =>
      it.segments
        .filter((seg) => seg.geometry.length >= 2)
        .map((seg) => ({
          color: seg.color,
          label: seg.kind === 'transit' ? `MHD ${seg.refId.startsWith('demo') ? '(demo)' : ''}`.trim() : it.name,
          dashed: !seg.realGeometry,
        })),
    );
    mapView.updateLegend(s.zonesEnabled ? zoneGrid : null, s.modes, segLegend);
  }

  /* ---------------- editor MHD ---------------- */
  function openTransit(): void {
    openTransitEditor(store, {
      onChanged: () => invalidatePlan(),
      requestStopPick: (cb) => {
        stopPickCallback = cb;
        mapView.setPickTarget('stop', mapWrap);
      },
    });
  }

  /* ---------------- subscribe ---------------- */
  let prevTheme = store.get().theme;
  store.subscribe((s) => {
    if (s.theme !== prevTheme) {
      prevTheme = s.theme;
      document.documentElement.setAttribute('data-theme', s.theme);
      mapView.setTheme(s.theme);
    }
    scheduleReplan();
    scheduleZones();
  });

  /* ---------------- klávesové zkratky ---------------- */
  document.addEventListener('keydown', (e) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return;
    if (e.key === 'd' || e.key === 'D') {
      themeBtn.click();
    }
  });

  /* ---------------- start ---------------- */
  if (!store.get().onboarded) openOnboarding(store);

  // Při prázdném oknu odjezdu („od teď") udržujeme výsledky čerstvé.
  window.setInterval(() => {
    if (depWindowEmpty() && store.get().origin && store.get().destination) {
      planHash = '';
      replan();
    }
  }, 60_000);

  window.addEventListener('online', () => {
    renderStatus();
    invalidatePlan();
  });
  window.addEventListener('offline', renderStatus);
  window.addEventListener('resize', () => mapView.invalidate());

  // Když je sdílený stav v URL, vycentrovat na body.
  const s0 = store.get();
  if (s0.origin || s0.destination) {
    mapView.fitToPoints(s0.origin, s0.destination);
  }
  replan();
}


function modeSubtitle(kind: string, speed: number): string {
  if (kind === 'pt') return 'dle jízdního řádu';
  if (kind === 'combined') return 'kombinace';
  return `~${Math.round(speed)} km/h`;
}
